import React, { useCallback, useEffect, useState } from 'react';
import { parse } from 'date-fns';
import { useIntl } from 'react-intl';
import { TextField, Tooltip } from '@mui/material';
import { TbIcon } from 'components/Figma/TbIcon';

import { isValidDate } from '../../utils/dateRange';
import { DateInputsProps, CUSTOM_DATE_INPUT_NAME } from './DateInputs.types';

const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';
/* eslint-disable react/jsx-props-no-spreading */
export function DateInputs({
  onChange,
  from,
  to,
  today,
  onValidate,
  validationData,
  readOnly,
  readOnlyTo,
  disabledDates,
  activeField,
  rangeKey,
  onFocus,
}: DateInputsProps) {
  const intl = useIntl();
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');

  const getLocalizedDate = useCallback((date: Date | undefined) => date?.toLocaleDateString('en-GB') ?? '', []);

  const createBlurHandler =
    (inputName: 'from' | 'to'): React.FocusEventHandler<HTMLInputElement> =>
    event => {
      let fromInputValue = fromValue;
      let toInputValue = toValue;
      const inputValue = event.target.value;

      if (inputName === 'from') {
        fromInputValue = inputValue;
        if (inputValue !== fromValue) {
          setFromValue(inputValue);
        }
      } else {
        toInputValue = inputValue;
        if (inputValue !== toValue) {
          setToValue(inputValue);
        }
      }

      const expectedFromValue = isValidDate(from) ? getLocalizedDate(from) : '';
      const expectedToValue = isValidDate(to) ? getLocalizedDate(to) : '';
      if (
        fromInputValue === expectedFromValue &&
        toInputValue === expectedToValue &&
        (from === undefined || isValidDate(from)) &&
        (to === undefined || isValidDate(to))
      ) {
        return;
      }

      const parsedFrom =
        fromInputValue.trim() === '' && from === undefined
          ? undefined
          : parse(fromInputValue, DEFAULT_DATE_FORMAT, new Date());
      const parsedTo =
        toInputValue.trim() === '' && to === undefined
          ? undefined
          : parse(toInputValue, DEFAULT_DATE_FORMAT, new Date());

      if (parsedFrom && parsedTo) {
        onValidate({ from: parsedFrom, to: parsedTo });
      }

      onChange(parsedFrom, parsedTo, 'custom', [], event);
    };

  const createChangeHandler =
    (inputName: 'from' | 'to'): React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> =>
    event => {
      if (inputName === 'from') {
        setFromValue(event.target.value);
      } else {
        setToValue(event.target.value);
      }
    };

  useEffect(() => {
    if (isValidDate(from)) {
      const fromString = getLocalizedDate(from);
      setFromValue(fromString);
    } else if (from === undefined) {
      setFromValue('');
    }

    if (isValidDate(to)) {
      const toString = getLocalizedDate(to);
      setToValue(toString);
    } else if (to === undefined) {
      setToValue('');
    }

    if (!!from && !!to) {
      const dateRangeToValidate = { from, to };
      onValidate(dateRangeToValidate);
    }
  }, [from, to, getLocalizedDate, onValidate, today, disabledDates]);

  const fromValidation =
    validationData.type === 'error' && validationData.from ? { helperText: validationData.from, error: true } : {};
  const toValidation =
    validationData.type === 'error' && validationData.to ? { helperText: validationData.to, error: true } : {};

  const fromField = (
    <div className="relative">
      <TextField
        {...fromValidation}
        fullWidth
        name={
          rangeKey === 'range' ? CUSTOM_DATE_INPUT_NAME.START_DATE_RANGE : CUSTOM_DATE_INPUT_NAME.START_DATE_COMPARISON
        }
        variant="outlined"
        label={intl.formatMessage({ id: 'datepicker.options.main.startDate', defaultMessage: 'Start date' })}
        onBlur={createBlurHandler('from')}
        onChange={createChangeHandler('from')}
        value={fromValue}
        type="text"
        inputProps={{ placeholder: 'dd/MM/yyyy', readOnly: readOnly }}
        focused={activeField.field === 'start' && activeField.range === rangeKey}
        onFocus={!readOnly ? () => onFocus('start', rangeKey) : () => {}}
        size="medium"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
          '&:hover': { borderColor: '#1a1a1a' },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1a1a1a' },
        }}
        FormHelperTextProps={{ style: { position: 'absolute', top: '100%', margin: '2px 0 0', whiteSpace: 'nowrap' } }}
      />
    </div>
  );

  const toField = (
    <div className="relative">
      <TextField
        {...toValidation}
        fullWidth
        name={rangeKey === 'range' ? CUSTOM_DATE_INPUT_NAME.END_DATE_RANGE : CUSTOM_DATE_INPUT_NAME.END_DATE_COMPARISON}
        variant="outlined"
        label={intl.formatMessage({ id: 'datepicker.options.main.endDate', defaultMessage: 'End date' })}
        onBlur={createBlurHandler('to')}
        onChange={createChangeHandler('to')}
        value={toValue}
        type="text"
        inputProps={{ placeholder: 'dd/MM/yyyy', readOnly: readOnly }}
        disabled={readOnlyTo}
        focused={activeField.field === 'end' && activeField.range === rangeKey && !readOnlyTo}
        onFocus={!(readOnly || readOnlyTo) ? () => onFocus('end', rangeKey) : () => {}}
        size="medium"
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
          '&:hover': { borderColor: '#1a1a1a' },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1a1a1a' },
        }}
        FormHelperTextProps={{ style: { position: 'absolute', top: '100%', margin: '2px 0 0', whiteSpace: 'nowrap' } }}
      />
    </div>
  );

  return (
    <div className="flex gap-4">
      <div className="w-[250px]">
        {readOnly ? (
          <Tooltip
            title={intl.formatMessage({
              id: 'datepicker.tooltip.notCustomizable',
              defaultMessage: 'Date range not customizable',
            })}
            placement="bottom-start"
            disableInteractive
          >
            <div>{fromField}</div>
          </Tooltip>
        ) : (
          fromField
        )}
      </div>
      <div className="flex items-center justify-center min-w-[24px]">
        <TbIcon name="ArrowRight" size="lg" />
      </div>
      <div className="w-[250px]">
        {readOnly ? (
          <Tooltip
            title={intl.formatMessage({
              id: 'datepicker.tooltip.notCustomizable',
              defaultMessage: 'Date range not customizable',
            })}
            placement="bottom-start"
            disableInteractive
          >
            <div>{toField}</div>
          </Tooltip>
        ) : (
          toField
        )}
      </div>
    </div>
  );
}
